import { CheckIcon, SparklesIcon } from "@heroicons/react/24/solid";

export interface StepDefinition {
  key: string;
  label: string;
}

type Props = {
  steps: StepDefinition[];
  currentStep: number;
  maxReachedStep: number;
  isStepComplete: (index: number) => boolean;
  onStepClick: (index: number) => void;
};

/**
 * Progress stepper for the evaluation wizard. Steps beyond maxReachedStep are visible
 * but not clickable - you can freely revisit anything you've already reached, but you
 * cannot skip ahead of the guided path. That gate lives here and in the Next button;
 * the final submit re-validates server-side regardless; see InterviewEvaluationController.
 */
export default function EvaluationStepper({
  steps,
  currentStep,
  maxReachedStep,
  isStepComplete,
  onStepClick,
}: Props) {
  return (
    <div className="mb-8">
      {/* Desktop / tablet: full labelled stepper */}
      <div className="hidden sm:flex items-start">
        {steps.map((step, i) => {
          const reachable = i <= maxReachedStep;
          const complete = isStepComplete(i);
          const isCurrent = i === currentStep;

          return (
            <div key={step.key} className="flex items-center flex-1 last:flex-none">
              <button
                type="button"
                disabled={!reachable}
                onClick={() => reachable && onStepClick(i)}
                className={`group flex flex-col items-center gap-1.5 ${
                  reachable ? "cursor-pointer" : "cursor-not-allowed"
                }`}
              >
                <span
                  className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-sm font-semibold transition-all duration-200 ${
                    isCurrent
                      ? "bg-entntblue text-white ring-4 ring-blue-100 dark:ring-blue-900/40 scale-110"
                      : complete
                        ? "bg-green-500 text-white group-hover:bg-green-600"
                        : reachable
                          ? "bg-gray-200 dark:bg-slate-700 text-gray-600 dark:text-gray-300 group-hover:bg-gray-300 dark:group-hover:bg-slate-600"
                          : "bg-gray-100 dark:bg-slate-800 text-gray-400 dark:text-gray-600"
                  }`}
                >
                  {complete && !isCurrent ? (
                    <CheckIcon className="h-5 w-5" />
                  ) : i === 0 ? (
                    <SparklesIcon className="h-4 w-4" />
                  ) : (
                    i
                  )}
                </span>
                <span
                  className={`text-[11px] font-medium whitespace-nowrap max-w-[90px] truncate ${
                    isCurrent
                      ? "text-entntblue"
                      : reachable
                        ? "text-gray-500 dark:text-gray-400"
                        : "text-gray-300 dark:text-gray-600"
                  }`}
                >
                  {step.label}
                </span>
              </button>

              {i < steps.length - 1 && (
                <div
                  className={`h-0.5 flex-1 mx-1.5 rounded-full transition-colors duration-300 ${
                    i < maxReachedStep
                      ? "bg-entntblue"
                      : "bg-gray-200 dark:bg-slate-700"
                  }`}
                />
              )}
            </div>
          );
        })}
      </div>

      {/* Mobile: compact label + progress bar */}
      <div className="sm:hidden">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">
            {steps[currentStep]?.label}
          </span>
          <span className="text-xs text-gray-400">
            Step {currentStep + 1} of {steps.length}
          </span>
        </div>
        <div className="h-1.5 w-full rounded-full bg-gray-200 dark:bg-slate-700 overflow-hidden">
          <div
            className="h-full bg-entntblue rounded-full transition-all duration-300 ease-out"
            style={{ width: `${((currentStep + 1) / steps.length) * 100}%` }}
          />
        </div>
      </div>
    </div>
  );
}
